import React from "react";
import { Helmet } from "react-helmet-async";
import { motion, Variants } from "framer-motion";
import {
  Home,
  Heart,
  Activity,
  TrendingDown,
  Wallet,
  UserMinus,
  Globe,
  Sparkles,
} from "lucide-react";

import img1 from "../../assets/oldage/mainimg.jpg";
import img2 from "../../assets/oldage/old-1.png";
import img3 from "../../assets/oldage/img1.webp";
import img4 from "../../assets/oldage/img2.jpg";
import img5 from "../../assets/oldage/img3.webp";
import img6 from "../../assets/oldage/img4.webp";

const siteUrl = "https://www.sevaafoundation.com";
const pageUrl = `${siteUrl}/old-age-homes`;
const defaultImage = `${siteUrl}/og-blogs.jpg`;
const logoImage = `${siteUrl}/logo512.png`;

const pageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  "@id": `${pageUrl}#webpage`,
  name: "Old Age Homes & Elderly Support | SEVAA Foundation",
  alternateName: "Ageing with Dignity - Elderly Care and Senior Citizen Support",
  url: pageUrl,
  inLanguage: "en-IN",
  description:
    "SEVAA Foundation supports elderly care through safe shelter, healthcare, nutrition, emotional support and dignity-based assistance for abandoned, poor and marginalized senior citizens.",
  isPartOf: {
    "@type": "WebSite",
    "@id": `${siteUrl}/#website`,
    name: "SEVAA Foundation",
    url: siteUrl,
  },
  publisher: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
    logo: {
      "@type": "ImageObject",
      url: logoImage,
    },
    image: defaultImage,
    slogan: "सेवा परमो धर्मः",
    areaServed: [
      { "@type": "Country", name: "India" },
      { "@type": "Place", name: "Jharkhand" },
      { "@type": "Place", name: "Chatra" },
      { "@type": "Place", name: "Pakur" },
    ],
  },
  about: [
    "Old Age Home Support",
    "Elderly Care",
    "Senior Citizen Support",
    "Safe Shelter",
    "Elderly Healthcare",
    "Elderly Nutrition",
    "Emotional Support",
    "Abandoned Elderly Support",
    "Elderly Women Support",
    "Ageing with Dignity",
    "Social Welfare"
  ],
  primaryImageOfPage: {
    "@type": "ImageObject",
    url: defaultImage,
    width: 1200,
    height: 630,
  },
};

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "@id": `${pageUrl}#service`,
  name: "Old Age Home and Elderly Care Support",
  serviceType: "Elderly Care and Senior Citizen Support",
  url: pageUrl,
  provider: {
    "@type": "NGO",
    "@id": `${siteUrl}/#ngo`,
    name: "SEVAA Foundation",
    alternateName: "Swadesh Sevaa Charitable Foundation",
    url: siteUrl,
  },
  areaServed: {
    "@type": "Country",
    name: "India",
  },
  description:
    "SEVAA Foundation provides elderly support through safe shelters, healthcare assistance, nutrition, emotional care and dignity-focused services for abandoned and marginalized senior citizens.",
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "@id": `${pageUrl}#breadcrumb`,
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${siteUrl}/`,
    },
    {
      "@type": "ListItem",
      position: 2,
      name: "Old Age Homes",
      item: pageUrl,
    },
  ],
};

const OldAgeHomes: React.FC = () => {
  const staggerContainerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const gridItemVariants: Variants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  return (
    <>
      <Helmet>
        <title>Old Age Home NGO | Elderly Care | SEVAA Foundation</title>

        <meta
          name="title"
          content="Old Age Home NGO | Elderly Care | SEVAA Foundation"
        />

        <meta
          name="description"
          content="Support SEVAA Foundation's elderly care mission for safe shelter, healthcare, nutrition, emotional support and dignity-based care for poor senior citizens."
        />

        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta name="author" content="SEVAA Foundation" />
        <meta name="theme-color" content="#0f766e" />
        <meta name="language" content="English, Hindi" />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SEVAA Foundation" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Old Age Home NGO | Elderly Care Support" />
        <meta property="og:description" content="Support elderly care, safe shelter, healthcare, nutrition and dignity-based support for abandoned and poor senior citizens." />
        <meta property="og:image" content={defaultImage} />
        <meta property="og:image:alt" content="SEVAA Foundation old age home and elderly care support program" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Old Age Home NGO | Elderly Care Support" />
        <meta name="twitter:description" content="Support SEVAA Foundation's old age home and elderly care mission for safe shelter, healthcare and dignity." />
        <meta name="twitter:image" content={defaultImage} />
        <meta name="twitter:image:alt" content="SEVAA Foundation old age home and elderly care support program" />

        <script type="application/ld+json">
          {JSON.stringify(pageSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <main className="bg-slate-50 min-h-screen font-poppins pb-20">
        {/* 1. HERO SECTION */}
        <section
          aria-labelledby="old-age-heading"
          className="relative w-full h-[500px] md:h-[600px]"
        >
          <div className="absolute inset-0">
            <img
              src={img1}
              alt="SEVAA Foundation elderly care and old age home support for senior citizens"
              className="w-full h-full object-cover"
              loading="eager"
              fetchPriority="high"
            />

            <div className="absolute inset-0 bg-gradient-to-r from-teal-900/70 to-black/60"></div>
          </div>

          <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-8 flex flex-col justify-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-3xl"
            >
              <span className="inline-block py-1 px-3 rounded-full bg-orange-500/20 text-orange-400 font-bold text-xs uppercase tracking-widest mb-4 border border-orange-500/30">
                Ageing with Dignity
              </span>

              <h1
                id="old-age-heading"
                className="text-4xl md:text-5xl lg:text-6xl font-black text-white tracking-tight leading-[1.1] mb-6"
              >
                Elderly Care & Support <br />
                <span className="text-orange-400">A Right to Respect</span>
              </h1>

              <p className="text-teal-50 text-base md:text-lg font-medium leading-relaxed mb-8 max-w-2xl opacity-90">
                SEVAA Foundation is dedicated to ensuring that abandoned,
                marginalized and poor elderly people live their later years with
                dignity, love, healthcare, nutrition and security.
              </p>
            </motion.div>
          </div>
        </section>

        {/* 2. THE HARSH REALITY */}
        <section
          aria-labelledby="elderly-reality-heading"
          className="max-w-7xl mx-auto px-6 lg:px-8 -mt-20 relative z-10 mb-20"
        >
          <h2 id="elderly-reality-heading" className="sr-only">
            Elderly Care Reality in India
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            <motion.article
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -12 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-3xl shadow-xl border border-slate-100 border-b-[6px] border-b-rose-500 group transition-all duration-300"
            >
              <TrendingDown className="w-12 h-12 text-rose-500 mb-6 group-hover:scale-110 transition-transform" />

              <h3 className="text-4xl font-black text-slate-900 mb-2">
                40<span className="text-xl">%+</span>
              </h3>

              <p className="text-slate-600 font-medium text-sm leading-relaxed">
                Of elderly people in India belong to the poorest wealth
                quintile, facing economic hardship, limited healthcare and
                dependency.
              </p>
            </motion.article>

            <motion.article
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -12 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-3xl shadow-xl border border-slate-100 border-b-[6px] border-b-amber-500 group transition-all duration-300"
            >
              <Wallet className="w-12 h-12 text-amber-500 mb-6 group-hover:scale-110 transition-transform" />

              <h3 className="text-4xl font-black text-slate-900 mb-2">
                18.7<span className="text-xl">%</span>
              </h3>

              <p className="text-slate-600 font-medium text-sm leading-relaxed">
                Of elderly individuals have no source of income, affecting their
                access to food, medicines, shelter and basic healthcare.
              </p>
            </motion.article>

            <motion.article
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ y: -12 }}
              viewport={{ once: true }}
              className="bg-teal-800 p-8 rounded-3xl shadow-xl border border-teal-700 text-white border-b-[6px] border-b-teal-400 group transition-all duration-300"
            >
              <UserMinus className="w-12 h-12 text-teal-300 mb-6 group-hover:scale-110 transition-transform" />

              <h3 className="text-2xl font-black mb-2">Women at Risk</h3>

              <p className="text-teal-100 font-medium text-sm leading-relaxed">
                Elderly women are particularly vulnerable due to lack of asset
                ownership, financial independence, family support and healthcare
                access.
              </p>
            </motion.article>
          </div>
        </section>

        {/* 3. THE GROUND REALITY */}
        <section
          aria-labelledby="elderly-struggle-heading"
          className="max-w-7xl mx-auto px-6 lg:px-8 py-16"
        >
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <div className="flex items-center gap-3">
                <span className="w-12 h-[2px] bg-teal-600"></span>

                <span className="text-teal-700 font-bold text-xs uppercase tracking-widest">
                  The Invisible Struggle
                </span>
              </div>

              <h2
                id="elderly-struggle-heading"
                className="text-3xl md:text-4xl font-black text-slate-900 leading-[1.2]"
              >
                Why our elderly are being left behind
              </h2>

              <p className="text-slate-600 leading-relaxed font-medium">
                Many elderly individuals, especially those in poor, rural and
                tribal regions, face financial insecurity, limited healthcare
                access, social neglect and loneliness.
              </p>

              <p className="text-slate-600 leading-relaxed font-medium">
                National social assistance schemes provide pensions, but gaps in
                implementation often leave the most disadvantaged senior
                citizens uncovered.
              </p>

              <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-2xl mt-8">
                <p className="text-slate-800 font-bold italic">
                  “SEVAA Foundation seeks to reach elderly people who are often
                  left behind, ensuring they live with dignity, care and
                  security.”
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative h-[500px] rounded-3xl overflow-hidden shadow-2xl"
            >
              <img
                src={img2}
                alt="SEVAA Foundation elderly support and senior citizen care"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </motion.div>
          </div>
        </section>

        {/* 4. SEVAA'S INTERVENTION */}
        <section
          aria-labelledby="old-age-strategy-heading"
          className="bg-white py-24 border-t border-slate-200 mt-10"
        >
          <div className="max-w-[90rem] mx-auto px-6 lg:px-8">
            <div className="text-center mb-16 max-w-3xl mx-auto">
              <h2
                id="old-age-strategy-heading"
                className="text-3xl md:text-4xl font-black text-slate-900 mb-4"
              >
                Homes & Support Strategy
              </h2>

              <p className="text-slate-500 font-medium text-lg">
                Providing holistic elderly care from clean shelters to
                healthcare, nutrition, emotional support and dignity-based
                wellbeing.
              </p>
            </div>

            <motion.div
              variants={staggerContainerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-100px" }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 auto-rows-fr"
            >
              <motion.article
                variants={gridItemVariants}
                className="relative rounded-[2rem] overflow-hidden shadow-xl group cursor-pointer min-h-[350px]"
              >
                <img
                  src={img3}
                  alt="Safe shelter for abandoned elderly by SEVAA Foundation"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />

                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300"></div>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="bg-slate-50 p-8 rounded-[2rem] shadow-lg border border-slate-100 flex flex-col justify-center hover:-translate-y-2 hover:shadow-orange-500/10 hover:border-orange-200 transition-all group min-h-[350px]"
              >
                <div className="w-14 h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-6 group-hover:bg-orange-50 transition-colors">
                  <Home className="w-7 h-7 text-orange-500" />
                </div>

                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  Safe Shelter
                </h3>

                <p className="text-slate-600 font-medium text-sm leading-relaxed">
                  Offering clean, comfortable and secure living facilities for
                  abandoned, poor and marginalized elderly people.
                </p>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="relative rounded-[2rem] overflow-hidden shadow-xl group cursor-pointer min-h-[350px]"
              >
                <img
                  src={img4}
                  alt="Health and nutrition support for elderly by SEVAA Foundation"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />

                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300"></div>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="bg-slate-50 p-8 rounded-[2rem] shadow-lg border border-slate-100 flex flex-col justify-center hover:-translate-y-2 hover:shadow-teal-500/10 hover:border-teal-200 transition-all group min-h-[350px]"
              >
                <div className="w-14 h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-6 group-hover:bg-teal-50 transition-colors">
                  <Activity className="w-7 h-7 text-teal-600" />
                </div>

                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  Health & Nutrition
                </h3>

                <p className="text-slate-600 font-medium text-sm leading-relaxed">
                  Ensuring regular checkups, medicines, healthy meals and
                  proper daily nutrition to maintain physical wellbeing.
                </p>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="relative rounded-[2rem] overflow-hidden shadow-xl group cursor-pointer min-h-[350px]"
              >
                <img
                  src={img5}
                  alt="Emotional support and companionship for elderly by SEVAA Foundation"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />

                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300"></div>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="bg-slate-50 p-8 rounded-[2rem] shadow-lg border border-slate-100 flex flex-col justify-center hover:-translate-y-2 hover:shadow-amber-500/10 hover:border-amber-200 transition-all group min-h-[350px]"
              >
                <div className="w-14 h-14 bg-white rounded-2xl shadow-sm flex items-center justify-center mb-6 group-hover:bg-amber-50 transition-colors">
                  <Heart className="w-7 h-7 text-amber-500" />
                </div>

                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  Emotional Support
                </h3>

                <p className="text-slate-600 font-medium text-sm leading-relaxed">
                  Creating a family-like environment with activities,
                  companionship and emotional care to reduce loneliness.
                </p>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="relative rounded-[2rem] overflow-hidden shadow-xl group cursor-pointer min-h-[350px]"
              >
                <img
                  src={img6}
                  alt="Dignity and respect for senior citizens by SEVAA Foundation"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  loading="lazy"
                />

                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300"></div>
              </motion.article>

              <motion.article
                variants={gridItemVariants}
                className="bg-teal-900 p-8 rounded-[2rem] shadow-lg flex flex-col justify-center hover:-translate-y-2 hover:shadow-teal-900/40 transition-all min-h-[350px] text-white overflow-hidden relative"
              >
                <div className="absolute -top-10 -right-10 w-32 h-32 bg-teal-800 rounded-full blur-2xl opacity-50"></div>

                <div className="w-14 h-14 bg-teal-800 rounded-2xl flex items-center justify-center mb-6 shadow-lg">
                  <Sparkles className="w-7 h-7 text-teal-300" />
                </div>

                <h3 className="text-xl font-bold mb-3">Our Mission</h3>

                <p className="text-teal-100 font-medium text-sm leading-relaxed mb-6">
                  Restoring respect and security for senior citizens. SEVAA
                  Foundation ensures elderly people never feel abandoned.
                </p>

                <a
                  href="/contact"
                  className="inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-2.5 rounded-full transition-colors text-sm w-fit"
                  aria-label="Support SEVAA Foundation elderly care mission"
                >
                  Give Respect
                  <Globe className="w-4 h-4" />
                </a>
              </motion.article>
            </motion.div>
          </div>
        </section>

      </main>
    </>
  );
};

export default OldAgeHomes;